// The ferrule shaders, shared by the Design Studio and the Find Your Ferrule so the preview in one looks exactly like the preview in the other.
//
// These are GLSL source strings and one threshold table, nothing else. They were
// lifted verbatim out of sections/ferrule-configurator.liquid; the comments
// inside them describe hard-won behaviour, so change them here and both tools
// move together.

window.FERAL_SHADERS = (function () {

const VERT = `
  varying vec3 vWorldNormal;
  varying vec3 vLocalNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;
  varying vec3 vViewPos;
  void main() {
    vLocalPos    = position;
    vLocalNormal = normal;
    vec4 wp      = modelMatrix * vec4(position, 1.0);
    vWorldPos    = wp.xyz;
    vWorldNormal = normalize(normalMatrix * normal);
    vec4 mvp     = modelViewMatrix * vec4(position, 1.0);
    vViewPos     = -mvp.xyz;
    gl_Position  = projectionMatrix * mvp;
  }
`;

const FRAG = `
  uniform vec3  uSurface;
  uniform vec3  uFeature;
  uniform vec3  uFeature2;
  uniform vec3  uFeature3;
  uniform float uGradient;
  uniform float uFadeSplit;
  uniform float uLocalYMin;
  uniform float uLocalYMax;
  uniform vec2  uAxisXZ;
  uniform float uRawBoreR;
  uniform float uFeatTh;
  uniform sampler2D uTex;
  uniform bool uHasTex;
  uniform bool uFlipH;
  uniform bool uFlipV;

  // Where the two colours hand over, and how wide the blend is.
  //
  // uFadeSplit is the share of the model the first colour keeps: 0.7 means
  // seven tenths of the way up before the second colour takes over. The blend
  // band opens outward from the split as far as it can reach, so at 0.5 it
  // spans the whole model and reproduces the plain linear mix this had before
  // the control existed - an untouched build looks exactly as it did.
  float feralFade(float t, float split) {
    float soft = max(2.0 * min(split, 1.0 - split), 0.08);
    float a = split - soft * 0.5;
    float b = split + soft * 0.5;
    return clamp((t - a) / max(b - a, 0.0001), 0.0, 1.0);
  }
  // Three evenly spaced stops: c1 at t=0, c2 at t=0.5, c3 at t=1. Deliberately
  // not routed through feralFade - that function exists to let the two-colour
  // split move, and with three stops there is nothing to move.
  vec3 feralFade3(float t, vec3 c1, vec3 c2, vec3 c3) {
    return t < 0.5 ? mix(c1, c2, t * 2.0) : mix(c2, c3, (t - 0.5) * 2.0);
  }
  varying vec3 vWorldNormal;
  varying vec3 vLocalNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;
  varying vec3 vViewPos;
  void main() {
    vec2  lxz   = vec2(vLocalPos.x - uAxisXZ.x, vLocalPos.z - uAxisXZ.y);
    float lr    = length(lxz);
    vec3  lrad  = lr > 0.001 ? vec3(lxz.x/lr, 0.0, lxz.y/lr) : vec3(1.0,0.0,0.0);
    vec3  LN    = normalize(vLocalNormal);
    float rdot  = dot(LN, lrad);
    float vabs  = abs(LN.y);
    bool isBore    = lr < uRawBoreR;
    bool isEndCap  = vabs > 0.75;
    bool isFeature = !isBore && !isEndCap && rdot < uFeatTh;
    vec3 base;
    if (uHasTex && !isBore && !isEndCap) {
      float ang = atan(vLocalPos.z - uAxisXZ.y, vLocalPos.x - uAxisXZ.x);
      float tu = 1.0 - (ang / (2.0 * 3.14159265359) + 0.5);
      float span2 = uLocalYMax - uLocalYMin;
      float tv = span2 > 0.001 ? clamp((vLocalPos.y - uLocalYMin) / span2, 0.0, 1.0) : 0.0;
      if (uFlipH) tu = 1.0 - tu;
      if (uFlipV) tv = 1.0 - tv;
      base = texture2D(uTex, vec2(tu, tv)).rgb;
    } else {
      vec3 featColor = uFeature;
      if (uGradient > 0.5) {
        float span = uLocalYMax - uLocalYMin;
        float t = span > 0.001 ? clamp((vLocalPos.y - uLocalYMin) / span, 0.0, 1.0) : 0.0;
        featColor = (uGradient > 1.5)
          ? feralFade3(t, uFeature, uFeature2, uFeature3)
          : mix(uFeature, uFeature2, feralFade(t, uFadeSplit));
      }
      base = isFeature ? featColor : uSurface;
    }
    vec3 N  = normalize(vWorldNormal);
    vec3 V  = normalize(vViewPos);
    vec3 L1 = normalize(vec3(3.0, 5.0, 3.0));
    float d1 = max(dot(N,L1),0.0);
    float s1 = pow(max(dot(N,normalize(L1+V)),0.0),64.0);
    vec3 L2  = normalize(vec3(-3.0, 2.0,-2.0));
    float d2 = max(dot(N,L2),0.0);
    vec3 L3  = normalize(vec3(0.0,-2.0,-4.0));
    float d3 = max(dot(N,L3),0.0);
    vec3 col = 0.20*base + 0.68*d1*base + 0.45*s1*vec3(1.0)
             + 0.18*d2*base + 0.14*d3*vec3(1.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const MODEL_FEAT_TH = { pyramid:0.92, dimple:0.97 };

const STD_VERT = `
  varying vec3 vLocalPos;
  varying vec3 vLocalNormal;
  varying vec3 vNormal;
  varying vec3 vViewPos;
  void main() {
    vLocalPos    = position;
    vLocalNormal = normal;
    vNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewPos = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

const STD_FRAG = `
  uniform sampler2D uTex;
  uniform bool      uHasTex;
  uniform bool      uFlipH;
  uniform bool      uFlipV;

  // Where the two colours hand over, and how wide the blend is.
  //
  // uFadeSplit is the share of the model the first colour keeps: 0.7 means
  // seven tenths of the way up before the second colour takes over. The blend
  // band opens outward from the split as far as it can reach, so at 0.5 it
  // spans the whole model and reproduces the plain linear mix this had before
  // the control existed - an untouched build looks exactly as it did.
  float feralFade(float t, float split) {
    float soft = max(2.0 * min(split, 1.0 - split), 0.08);
    float a = split - soft * 0.5;
    float b = split + soft * 0.5;
    return clamp((t - a) / max(b - a, 0.0001), 0.0, 1.0);
  }
  // Three evenly spaced stops: c1 at t=0, c2 at t=0.5, c3 at t=1. Deliberately
  // not routed through feralFade - that function exists to let the two-colour
  // split move, and with three stops there is nothing to move.
  vec3 feralFade3(float t, vec3 c1, vec3 c2, vec3 c3) {
    return t < 0.5 ? mix(c1, c2, t * 2.0) : mix(c2, c3, (t - 0.5) * 2.0);
  }
  uniform vec3      uColor;
  uniform vec3      uColor2;
  uniform vec3      uColor3;
  uniform float     uGradient;
  uniform float     uFadeSplit;
  uniform float     uCylAxis;
  uniform float     uCylMin;
  uniform float     uCylRange;
  uniform float     uRadA;
  uniform float     uRadB;
  uniform float     uEndCapTh;
  varying vec3 vLocalPos;
  varying vec3 vLocalNormal;
  varying vec3 vNormal;
  varying vec3 vViewPos;
  const float PI = 3.14159265359;
  void main() {
    float da, db, cylPos;
    if (uCylAxis < 0.5) {
      da = vLocalPos.y - uRadA; db = vLocalPos.z - uRadB;
      cylPos = vLocalPos.x;
    } else if (uCylAxis < 1.5) {
      da = vLocalPos.x - uRadA; db = vLocalPos.z - uRadB;
      cylPos = vLocalPos.y;
    } else {
      da = vLocalPos.x - uRadA; db = vLocalPos.y - uRadB;
      cylPos = vLocalPos.z;
    }
    // Bore detection via inward-pointing local normal (robust vs taper/radius confusion)
    float rLen = sqrt(da*da + db*db);
    float radialN = 0.0;
    if (rLen > 0.0001) {
      vec2 radialDir = vec2(da, db) / rLen;
      if (uCylAxis < 0.5)      radialN = dot(vec2(vLocalNormal.y, vLocalNormal.z), radialDir);
      else if (uCylAxis < 1.5) radialN = dot(vec2(vLocalNormal.x, vLocalNormal.z), radialDir);
      else                     radialN = dot(vec2(vLocalNormal.x, vLocalNormal.y), radialDir);
    }
    bool isBore = radialN < -0.3;
    float axN;
    if (uCylAxis < 0.5)      axN = abs(vLocalNormal.x);
    else if (uCylAxis < 1.5) axN = abs(vLocalNormal.y);
    else                     axN = abs(vLocalNormal.z);
    bool isEndCap = axN > uEndCapTh;
    float u = 1.0 - (atan(db, da) / (PI * 2.0) + 0.5);
    float v = clamp((cylPos - uCylMin) / uCylRange, 0.0, 1.0);
    if (uFlipH) u = 1.0 - u;
    if (uFlipV) v = 1.0 - v;
    vec3 solidColor = (uGradient > 1.5)
      ? feralFade3(v, uColor, uColor2, uColor3)
      : ((uGradient > 0.5) ? mix(uColor, uColor2, feralFade(v, uFadeSplit)) : uColor);
    vec3 baseColor = (uHasTex && !isBore && !isEndCap)
      ? texture2D(uTex, vec2(u, v)).rgb : solidColor;
    vec3 N = normalize(vNormal);
    vec3 V = normalize(-vViewPos);
    vec3 L1 = normalize(vec3(3.0, 5.0, 3.0));
    vec3 L2 = normalize(vec3(-2.0, 1.0, -2.0));
    float diff = max(dot(N, L1), 0.0) * 0.70 + max(dot(N, L2), 0.0) * 0.25;
    float spec = pow(max(dot(N, normalize(L1 + V)), 0.0), 40.0) * 0.30;
    gl_FragColor = vec4(baseColor * (0.55 + diff) + vec3(spec), 1.0);
  }
`;

return { VERT, FRAG, STD_VERT, STD_FRAG, MODEL_FEAT_TH };

})();
